/**
 * Cash Flow Statement — presentation only.
 *
 * Tells the money story for the selected period in the order an operator
 * thinks about it: what came in → where it went → what's left → what's still
 * owed → what we expect to have left.
 *
 * IMPORTANT: this component performs NO financial calculation. Every figure is
 * read straight off the canonical `financial-ledger` summary response. The only
 * arithmetic here is the display of already-canonical relationships that the
 * service itself guarantees:
 *   money in (+ corrections) − total money out = net cash activity
 *   net cash activity + expected revenue − remaining payroll − Grace owed = projected net
 */
import { Card, CardContent } from "@/components/ui/card";
import { ChevronRight } from "lucide-react";
import { money, type LedgerSummaryResponse } from "@/lib/financialLedger";

type Drill = (card: string) => void;

function Line({
  label, amountCents, sign, hint, count, onClick, muted,
}: {
  label: string;
  amountCents: number;
  sign: "+" | "-" | "none";
  hint?: string;
  count?: number;
  onClick?: () => void;
  muted?: boolean;
}) {
  const clickable = !!onClick;
  const prefix = sign === "+" ? "+" : sign === "-" ? "−" : "";
  return (
    <div
      className={`flex items-start justify-between gap-3 rounded-md px-2 py-1.5 ${
        clickable ? "cursor-pointer hover:bg-muted/60" : ""
      }`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="min-w-0">
        <p className={`text-sm ${muted ? "text-muted-foreground" : "text-foreground"} flex items-center gap-1`}>
          {label}
          {clickable && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
        </p>
        {(hint || count !== undefined) && (
          <p className="text-[11px] text-muted-foreground">
            {hint}
            {hint && count !== undefined ? " · " : ""}
            {count !== undefined ? `${count} transaction${count === 1 ? "" : "s"}` : ""}
          </p>
        )}
      </div>
      <p
        className={`text-sm tabular-nums font-medium shrink-0 ${
          sign === "+" ? "text-primary" : sign === "-" ? "text-destructive" : "text-foreground"
        }`}
      >
        {prefix}{money(Math.abs(amountCents))}
      </p>
    </div>
  );
}

function Total({ label, amountCents, sign, strong, onClick }: {
  label: string; amountCents: number; sign: "+" | "-" | "none"; strong?: boolean; onClick?: () => void;
}) {
  const prefix = sign === "+" ? "+" : sign === "-" ? "−" : "";
  const clickable = !!onClick;
  return (
    <div
      className={`flex items-center justify-between gap-3 border-t pt-2 mt-1 px-2 ${clickable ? "cursor-pointer hover:bg-muted/60 rounded-md" : ""}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <p className={`text-sm ${strong ? "font-heading font-bold" : "font-medium"}`}>{label}</p>
      <p className={`tabular-nums ${strong ? "text-xl font-heading font-bold" : "text-sm font-semibold"}`}>
        {prefix}{money(Math.abs(amountCents))}
      </p>
    </div>
  );
}

function SectionTitle({ children, note }: { children: React.ReactNode; note?: string }) {
  return (
    <div className="px-2">
      <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{children}</p>
      {note && <p className="text-[11px] text-muted-foreground">{note}</p>}
    </div>
  );
}

export function CashFlowStatement({
  summary, onDrill, onExplainPayroll, onExplainProjection,
}: {
  summary: LedgerSummaryResponse;
  onDrill: Drill;
  onExplainPayroll: () => void;
  onExplainProjection: () => void;
}) {
  const s = summary.summary;
  const sc = summary.source_counts ?? {};
  const lines = summary.projected_net_breakdown ?? [];

  // Read the canonical breakdown rather than recomputing anything.
  const findLine = (needle: string) =>
    lines.find((l) => l.label.toLowerCase().includes(needle))?.amount_cents ?? 0;
  const correctionsIn = findLine("corrections returned");
  const expectedRevenue = findLine("expected revenue");

  // Grace paid inside this period is real cash out; Grace still accrued is not.
  const gracePaid = s.grace_paid_cents;

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {/* ---------- Actual cash movement ---------- */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-1">
            <SectionTitle note="Cash that actually moved during this period">Money In</SectionTitle>
            <Line
              label="Actual money received"
              amountCents={s.actual_revenue_cents}
              sign="+"
              hint="Registration payments collected"
              count={sc.actual_revenue}
              onClick={() => onDrill("actual_revenue")}
            />
            {correctionsIn !== 0 && (
              <Line label="Corrections returned to the Hub" amountCents={correctionsIn} sign="+" />
            )}
            {correctionsIn !== 0 && (
              <Total label="Total Money In" amountCents={s.actual_revenue_cents + correctionsIn} sign="+" />
            )}
          </div>

          <div className="space-y-1">
            <SectionTitle note="Money that has already left the account">Money Out</SectionTitle>
            <Line
              label="Payroll paid"
              amountCents={s.payroll_paid_cents}
              sign="-"
              hint="Instructor payments confirmed"
              count={sc.payroll_paid}
              onClick={() => onDrill("payroll_paid")}
            />
            <Line
              label="Refunds sent"
              amountCents={s.refunds_cents}
              sign="-"
              count={sc.refunds}
              onClick={() => onDrill("refunds")}
            />
            {gracePaid !== 0 && (
              <Line
                label="Grace facility share paid"
                amountCents={gracePaid}
                sign="-"
                count={sc.grace_paid}
                onClick={() => onDrill("grace_paid")}
              />
            )}
            <Line
              label="Supplies"
              amountCents={s.supplies_cents}
              sign="-"
              count={sc.supplies}
              onClick={() => onDrill("supplies")}
            />
            <Line
              label="Other expenses"
              amountCents={s.other_expenses_cents}
              sign="-"
              count={sc.other_expenses}
              onClick={() => onDrill("other_expenses")}
            />
            <Total
              label="Total Money Out"
              amountCents={s.actual_expenses_cents}
              sign="-"
              onClick={() => onDrill("actual_expenses")}
            />
          </div>

          <Total
            label="Net Cash Activity"
            amountCents={s.net_cash_activity_cents}
            sign={s.net_cash_activity_cents < 0 ? "-" : "+"}
            strong
            onClick={() => onDrill("net_cash_activity")}
          />
          <p className="text-[11px] text-muted-foreground px-2">
            Money In minus Total Money Out. Nothing accrued or expected is included here.
          </p>
        </CardContent>
      </Card>

      {/* ---------- Obligations + projection ---------- */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-1">
            <SectionTitle note="Accrued or expected — not yet paid, and never counted as Money Out">
              Still Owed / Upcoming
            </SectionTitle>
            <Line
              label="Grace facility share accrued"
              amountCents={s.grace_outstanding_cents}
              sign="none"
              hint={`Still owed for the selected period · ${money(s.grace_paid_cents)} already paid`}
              count={sc.grace_paid}
              muted
              onClick={() => onDrill("grace_paid")}
            />
            <Line
              label="Remaining instructor payroll"
              amountCents={s.payroll_outstanding_cents}
              sign="none"
              hint="Meetings earned but not yet paid — click to see who"
              muted
              onClick={onExplainPayroll}
            />
            {expectedRevenue !== 0 && (
              <Line
                label="Expected revenue still to collect"
                amountCents={expectedRevenue}
                sign="none"
                muted
              />
            )}
            <Total label="Total known obligations still owed" amountCents={s.outstanding_obligations_cents} sign="none" />
          </div>

          <div className="space-y-1">
            <SectionTitle note="What the Hub expects to retain once the items above settle">
              Projected Position
            </SectionTitle>
            <Line
              label="Current net cash activity"
              amountCents={s.net_cash_activity_cents}
              sign={s.net_cash_activity_cents < 0 ? "-" : "+"}
            />
            {expectedRevenue !== 0 && (
              <Line label="Expected revenue still to collect" amountCents={expectedRevenue} sign="+" />
            )}
            <Line label="Grace facility share still owed" amountCents={s.grace_outstanding_cents} sign="-" />
            <Line label="Remaining payroll due" amountCents={s.payroll_outstanding_cents} sign="-" />
            <Total
              label="Projected net after known obligations"
              amountCents={s.projected_net_cents}
              sign={s.projected_net_cents < 0 ? "-" : "+"}
              strong
              onClick={onExplainProjection}
            />
          </div>
          <p className="text-[11px] text-muted-foreground px-2">
            Projected figures are never recorded as cash. When Grace or payroll is actually paid, it moves into
            Money Out automatically through its own record.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
